// Sends checkout telemetry to the backend (POST /checkout_events) so
// client-side failures show up in the falcon admin checkout events log
// next to the server-side ones. Every call is fire-and-forget and
// swallows its own errors: tracking must never break a checkout.

const ENDPOINT = "/checkout_events";
const MAX_STACK_LENGTH = 2000;

// A single runaway loop used to post the identical error dozens of times in
// one minute. That buries the rest of the log and burns the rack_attack
// budget, so the genuinely different event that follows gets 429'd and lost.
export const MAX_REPEATS_PER_MESSAGE = 3;

const seenCounts = new Map();

export function resetCheckoutEventDeduplication() {
  seenCounts.clear();
}

function withinRepeatLimit(event, message) {
  const key = `${event}:${message}`;
  const count = (seenCounts.get(key) ?? 0) + 1;
  seenCounts.set(key, count);

  return count <= MAX_REPEATS_PER_MESSAGE;
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

function detectFlow() {
  return window.location.pathname.startsWith("/shop") ? "shop" : "course";
}

// Both checkout flows carry the order uid in the path:
//   /courses/:course_id/orders/:order_uid/checkout   (course, Vue)
//   /shop/orders/:order_uid/checkout                 (shop, Alpine)
// Reading it here means every client event links to its order without each
// call site remembering to pass it. The order column is what lets the show
// page rebuild a buyer's checkout timeline.
export function detectOrderUid() {
  return window.location.pathname.match(/\/orders\/([^/]+)\/checkout/)?.[1] ?? null;
}

export function trackCheckoutEvent(
  event,
  {
    severity = "info",
    message = null,
    context = {},
    orderUid = detectOrderUid(),
    flow = detectFlow(),
  } = {}
) {
  try {
    const headers = { "Content-Type": "application/json" };
    const token = csrfToken();
    if (token) headers["X-CSRF-Token"] = token;

    return fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ event, severity, message, context, order_uid: orderUid, flow }),
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  } catch {
    return Promise.resolve();
  }
}

// A superseded request is normal control flow, not a fault: the checkout
// cancels an in-flight call whenever a newer one replaces it. Logging those
// as errors buries the real faults and burns the rack_attack budget, so they
// never reach the log.
export function isCancellation(reason) {
  if (!reason) return false;
  if (reason.__CANCEL__ === true) return true; // axios Cancel / CanceledError
  if (reason.name === "AbortError" || reason.name === "CanceledError") return true;

  return false;
}

let captureInstalled = false;

// Reports uncaught JS errors and unhandled promise rejections that happen
// while the user is on a checkout page.
export function installCheckoutErrorCapture({
  orderUid = detectOrderUid(),
  flow = detectFlow(),
} = {}) {
  if (captureInstalled) return;
  captureInstalled = true;

  window.addEventListener("error", (e) => {
    // The error event also fires for failed subresource loads (a 404 on an
    // image or script), which carry no message and no Error object. Those are
    // not JS faults and would otherwise show up as blank client_js_error rows.
    if (!(e instanceof ErrorEvent)) return;
    if (!withinRepeatLimit("client_js_error", e.message)) return;

    trackCheckoutEvent("client_js_error", {
      severity: "error",
      message: e.message,
      context: {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        // Without the stack, a throw from an inline script reports only the
        // document URL and a line number that shifts with the rendered page,
        // which is not enough to name the culprit.
        name: e.error?.name,
        stack: e.error?.stack?.slice(0, MAX_STACK_LENGTH),
      },
      orderUid,
      flow,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    if (isCancellation(reason)) return;

    const message = (reason && reason.message) || String(reason);
    if (!withinRepeatLimit("client_unhandled_rejection", message)) return;

    trackCheckoutEvent("client_unhandled_rejection", {
      severity: "error",
      message,
      context: {
        name: reason?.name,
        stack: reason?.stack?.slice(0, MAX_STACK_LENGTH),
      },
      orderUid,
      flow,
    });
  });
}
